import { useEffect, useMemo, useState } from "react";
import { Phone, Mail, MessageCircle, User, Calendar, Flag, Plus, X, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TYPE_META = {
  call:      { icon: Phone,         label: "Call",         color: "#22C55E" },
  email:     { icon: Mail,          label: "Email",        color: "#3B82F6" },
  message:   { icon: MessageCircle, label: "Message",      color: "#A855F7" },
  in_person: { icon: User,          label: "In person",    color: "#F97316" },
  other:     { icon: Flag,          label: "Other",        color: "#94A3B8" },
  chat:      { icon: MessageCircle, label: "In-app chat",  color: "#A855F7" },
  flag_cleared:  { icon: Flag,      label: "NEW badge cleared",   color: "#EF4444" },
  flag_restored: { icon: Flag,      label: "NEW badge restored",  color: "#EF4444" },
  appointment: { icon: Calendar,    label: "Appointment",  color: "#f47a20" },
};

function relDay(iso) {
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diff <= 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff}d ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function ContactTimeline({ patientId, patientName, days = 30 }) {
  const { user } = useAuth();
  const [events, setEvents] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "call", direction: "outbound", note: "", visible_to_patient: false });
  const [filter, setFilter] = useState("all");            // all | call | email | message | in_person | appointment | system
  const [visibleOnly, setVisibleOnly] = useState(false);  // patient-visible entries only

  const load = () => api.get(`/clinician/patients/${patientId}/timeline?days=${days}`)
    .then((r) => setEvents(r.data.events || []))
    .catch(() => setEvents([]));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId, days]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/clinician/patients/${patientId}/contact-log`, form);
      toast.success("Contact logged");
      setForm({ type: "call", direction: "outbound", note: "", visible_to_patient: false });
      setShowForm(false);
      load();
    } catch { toast.error("Could not log contact"); }
    finally { setSaving(false); }
  };

  const remove = async (rawId) => {
    if (!window.confirm("Delete this contact entry?")) return;
    try {
      await api.delete(`/clinician/contact-log/${rawId}`);
      toast.success("Entry deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  // Timeline filters — client-side only, no extra API call. `visibleOnly`
  // keeps manual entries flagged `visible_to_patient=true`; auto-collected
  // events (appointments, system, chat) are dropped when the filter is on.
  const filteredEvents = useMemo(() => {
    if (!events) return null;
    return events.filter((e) => {
      if (visibleOnly && !(e.source === "manual" && e.visible_to_patient === true)) return false;
      if (filter === "all") return true;
      if (filter === "appointment") return e.source === "appointment";
      if (filter === "system") return e.source === "system";
      if (filter === "message") return e.source === "messages" || e.type === "message";
      return e.type === filter;
    });
  }, [events, filter, visibleOnly]);

  const grouped = useMemo(() => {
    if (!filteredEvents) return [];
    const map = new Map();
    for (const e of filteredEvents) {
      const key = new Date(e.at).toISOString().slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filteredEvents]);

  return (
    <div className="space-y-4" data-testid="contact-timeline">
      <div className="flex items-center justify-between">
        <div>
          <div className="fl-label text-[#f47a20]">Contact timeline · last {days} days</div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {patientName ? `${patientName} · ` : ""}auto-collected + manual entries
          </div>
        </div>
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="fl-btn text-xs"
            data-testid="timeline-log-open"
          >
            <Plus size={12} /> Log contact
          </button>
        ) : (
          <button onClick={() => setShowForm(false)} className="fl-btn-ghost text-xs" data-testid="timeline-log-cancel">
            <X size={12} /> Cancel
          </button>
        )}
      </div>

      {/* Quick filter chips */}
      {events && events.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="timeline-filters">
          {[
            { k: "all",         l: "All" },
            { k: "call",        l: "Calls" },
            { k: "email",       l: "Emails" },
            { k: "message",     l: "Messages" },
            { k: "in_person",   l: "In person" },
            { k: "appointment", l: "Appointments" },
            { k: "system",      l: "System" },
          ].map(({ k, l }) => {
            const count = k === "all"
              ? events.length
              : events.filter((e) =>
                  k === "appointment" ? e.source === "appointment" :
                  k === "system"      ? e.source === "system" :
                  k === "message"     ? (e.source === "messages" || e.type === "message") :
                                        e.type === k
                ).length;
            const active = filter === k;
            return (
              <button
                key={k}
                onClick={() => setFilter(k)}
                disabled={count === 0 && k !== "all"}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                  active
                    ? "bg-[#f47a20] text-black border-[#f47a20]"
                    : count === 0
                      ? "text-zinc-600 border-white/5 cursor-not-allowed"
                      : "text-zinc-300 border-white/10 hover:border-[#f47a20]/60 hover:text-white"
                }`}
                data-testid={`timeline-filter-${k}`}
              >
                {l} {count > 0 && <span className="text-[9px] opacity-70 ml-0.5">{count}</span>}
              </button>
            );
          })}
          <label
            className={`text-[11px] px-2.5 py-1 rounded-full border transition cursor-pointer flex items-center gap-1.5 ml-1 ${
              visibleOnly
                ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-300"
                : "text-zinc-300 border-white/10 hover:border-emerald-500/40"
            }`}
            data-testid="timeline-filter-visible-only"
          >
            <input
              type="checkbox"
              checked={visibleOnly}
              onChange={(e) => setVisibleOnly(e.target.checked)}
              className="w-3 h-3 accent-emerald-500"
            />
            Patient-visible only
          </label>
        </div>
      )}

      {showForm && (
        <div className="fl-card p-4 space-y-3 border-[#f47a20]/40" data-testid="timeline-log-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="fl-label mb-1 block">Type</label>
              <select
                className="fl-input w-full"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                data-testid="timeline-log-type"
              >
                <option value="call">Call</option>
                <option value="email">Email</option>
                <option value="message">Message (external)</option>
                <option value="in_person">In person</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="fl-label mb-1 block">Direction</label>
              <select
                className="fl-input w-full"
                value={form.direction}
                onChange={(e) => setForm({ ...form, direction: e.target.value })}
                data-testid="timeline-log-direction"
              >
                <option value="outbound">Outbound (I contacted them)</option>
                <option value="inbound">Inbound (they contacted me)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="fl-label mb-1 block">Note (optional)</label>
            <textarea
              className="fl-input w-full"
              rows={2}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="e.g. Left voicemail — will retry tomorrow"
              data-testid="timeline-log-note"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
            <input
              type="checkbox"
              checked={form.visible_to_patient}
              onChange={(e) => setForm({ ...form, visible_to_patient: e.target.checked })}
              data-testid="timeline-log-visible"
            />
            Share this entry with the patient
          </label>
          <button
            onClick={save}
            disabled={saving}
            className="fl-btn text-sm w-full"
            data-testid="timeline-log-save"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Save entry
          </button>
        </div>
      )}

      {events === null ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : events.length === 0 ? (
        <div className="text-zinc-500 text-sm py-6 text-center border border-dashed border-white/10 rounded">
          No touchpoints in the last {days} days. Log the first one above.
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="text-zinc-500 text-sm py-6 text-center border border-dashed border-white/10 rounded" data-testid="timeline-empty-filtered">
          No entries match this filter.
          <button
            onClick={() => { setFilter("all"); setVisibleOnly(false); }}
            className="ml-2 text-[#f47a20] hover:underline"
            data-testid="timeline-filter-reset"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([day, items]) => (
            <div key={day}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                {relDay(items[0].at)} · {new Date(day).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
              </div>
              <div className="space-y-2">
                {items.map((e) => {
                  const meta = TYPE_META[e.type] || TYPE_META.other;
                  const Icon = meta.icon;
                  const canDelete = e.source === "manual" && (user?.role === "admin" || true);
                  return (
                    <div
                      key={e.id}
                      className="flex items-start gap-3 p-3 rounded border border-white/5 bg-white/[0.02] hover:bg-white/5 transition-colors"
                      data-testid={`timeline-event-${e.id}`}
                    >
                      <div
                        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: `${meta.color}22`, color: meta.color }}
                      >
                        <Icon size={13} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-zinc-100">{meta.label}</span>
                          {e.direction && (
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500">· {e.direction.replace("_", " ")}</span>
                          )}
                          <span className="text-[10px] text-zinc-500 ml-auto">
                            {new Date(e.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                        {e.note && <div className="text-xs text-zinc-400 mt-1">{e.note}</div>}
                        {e.by_name && (
                          <div className="text-[10px] text-zinc-500 mt-1">by {e.by_name}</div>
                        )}
                      </div>
                      {canDelete && e.raw_id && (
                        <button
                          onClick={() => remove(e.raw_id)}
                          className="text-zinc-500 hover:text-red-400 shrink-0"
                          title="Delete entry"
                          data-testid={`timeline-delete-${e.raw_id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
